import React, { useEffect, useState } from "react";
import { FaArrowRight } from "react-icons/fa";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import type { E164Number } from "libphonenumber-js/core";
import axios from "axios";
import { z } from "zod";
import { decryptData } from "../../utils/decrypt";
import { isValidPhoneNumber, parsePhoneNumber } from "react-phone-number-input";


interface Agenda {
  agendaId: number;
  title: string;
  mail: boolean;
  createdAt: string;
  updatedAt: string;
}

interface MeetingRequestFormProps {
  agendas: Agenda[];
  onFormSubmit: () => void;
}

// Zod validation schema
const attendeeSchema = z.object({
    name: z
    .string()
    .min(2, "Name is required")
    .regex(/^[A-Za-z\s'-]+$/, "Invalid name"),

  email: z.email("Invalid email address"),

  department: z
    .string()
    .min(2, "Department is required")
    .regex(/^[A-Za-z\s]+$/, "Invalid department"),
});

const nameRegex = /^[A-Za-z\s'-]+$/;
const textRegex = /^[A-Za-z0-9\s.,'-]+$/;

const formSchema = z.object({
  fullName: z
    .string()
    .min(2, "Full name is required")
    .regex(nameRegex, "Name can only contain letters"),
  email: z.email("Invalid email address"),
  title: z
    .string()
    .min(2, "Title is required")
    .regex(textRegex, "Invalid characters in title"),
  organization: z
    .string()
    .min(2, "Organization is required")
    .regex(textRegex, "Invalid characters"),
phoneNumber: z
  .string()
  .min(1, "Phone number is required")
  .refine((value) => isValidPhoneNumber(value), {
    message: "Invalid phone number",
  }),
  date: z.string().min(1, "Date is required"),
  selectedAgenda: z.string().min(1, "Please select an agenda"),
  meetingAgenda: z.string().min(1, "Meeting agenda is required"),
  additionalNotes: z.string().optional(),
  attendees: z.array(attendeeSchema).min(1, "At least one attendee is required"),
});

const MeetingRequestForm = ({
  agendas,
  onFormSubmit,
}: MeetingRequestFormProps) => {
  console.log("agendas:", agendas);
console.log("isArray:", Array.isArray(agendas));
  const [attendees, setAttendees] = useState([
    { name: "", email: "", department: "" },
  ]);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    title: "",
    organization: "",
    phoneNumber: undefined as E164Number | undefined,
    meetingAgenda: "",
    date: "",
    selectedAgenda: "",
    additionalNotes: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
const [attendeeErrors, setAttendeeErrors] = useState<
  Record<number, { name?: string; email?: string; department?: string }>
>({});
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  // const captchaRef = useRef<ReCAPTCHA>(null);
  const [website, setWebsite] = useState("");

// const [captchaToken, setCaptchaToken] = useState("");

useEffect(() => {
  const getCaptchaKey = async () => {
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/meeting-request/captcha-key`
      );
     const decrypted = decryptData(res.data, res.data.iv);
       
      console.log("res data",decrypted.token)

      setCaptchaToken(decrypted.token);

    // setNonce(res.data.nonce);
    // setTimestamp(res.data.timestamp);
    // setChecksum(res.data.checksum);
    } catch (err) {
      console.error("Captcha key error", err);
    }
  };

  getCaptchaKey();
}, []);

  const handleAddAttendee = () => {
    if (attendees.length < 4) {
      setAttendees([...attendees, { name: "", email: "", department: "" }]);
    }
  };

const handleAttendeeChange = (
  index: number,
  field: string,
  value: string
) => {
  const newAttendees = [...attendees];
  newAttendees[index] = { ...newAttendees[index], [field]: value };
  setAttendees(newAttendees);

  const result = attendeeSchema.safeParse(newAttendees[index]);

  if (!result.success) {
    const fieldError = result.error.issues.find(
      (err) => err.path[0] === field
    );

    if (fieldError) {
      setAttendeeErrors((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          [field]: fieldError.message,
        },
      }));
    } else {
      // remove error if fixed
      setAttendeeErrors((prev) => {
        const updated = { ...prev };
        if (updated[index]) {
          delete updated[index][field];
        }
        return updated;
      });
    }
  } else {
    // all valid → clear error
    setAttendeeErrors((prev) => {
      const updated = { ...prev };
      if (updated[index]) {
        delete updated[index][field];
      }
      return updated;
    });
  }
};

  const handleRemoveAttendee = (index: number) => {
    if (attendees.length > 1) {
      const newAttendees = attendees.filter((_, i : any) => i !== index);
      setAttendees(newAttendees);
    }
  };

//   const handleCaptchaChange = (token: string | null) => {
//   setCaptchaToken(token);
// };

const handleChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
) => {
  const { name, value } = e.target;

  const updatedData = { ...formData, [name]: value };
  setFormData(updatedData);

  const result = formSchema.safeParse({
    ...updatedData,
    phoneNumber: updatedData.phoneNumber || "",
    attendees,
  });

  if (!result.success) {
    const fieldError = result.error.issues.find(
      (err) => err.path[0] === name
    );

    if (fieldError) {
      setErrors((prev) => ({
        ...prev,
        [name]: fieldError.message,
      }));
    } else {
      // remove error if fixed
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  } else {
    // all valid → clear error
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  }
};

  const handleAgendaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedAgendaId = e.target.value;
    const selectedAgenda = agendas.find(
      (agenda) => agenda.agendaId === parseInt(selectedAgendaId)
    );
    setFormData((prevFormData) => ({
      ...prevFormData,
      selectedAgenda: selectedAgendaId,
      meetingAgenda: selectedAgenda ? selectedAgenda.title : "",
    }));
  };

const handlePhoneChange = (value?: E164Number) => {
  const phone = value || "";
  setFormData({ ...formData, phoneNumber: value });

  if (!phone) {
    setErrors((prev) => ({
      ...prev,
      phoneNumber: "Phone number is required",
    }));
    return;
  }

  // Optional: detect country-specific length while typing
  try {
    const parsed = parsePhoneNumber(phone);

   if (parsed && !parsed.isValid()) {
  setErrors((prev) => ({
    ...prev,
    phoneNumber: `Invalid phone number for ${parsed.country}`,
  }));
} else {
      // valid → remove error
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.phoneNumber;
        return newErrors;
      });
    }
  } catch {
    setErrors((prev) => ({
      ...prev,
      phoneNumber: "Invalid phone number",
    }));
  }
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

//     if (!captchaToken) {
//   alert("Please complete the captcha");
//   setIsLoading(false);
//   return;
// }

    setIsLoading(true); // Start loading
    setErrors({});
    setAttendeeErrors({});

    // Prepare data for validation
    const validationData = {
      ...formData,
      phoneNumber: formData.phoneNumber || "",
      attendees,
    };

    try {
      // Validate the form data
      formSchema.parse(validationData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        const attendeeFieldErrors: Record<number, any> = {};

error.issues.forEach((err: z.ZodIssue) => {
  if (err.path[0] === "attendees") {
    const index = err.path[1] as number;
    const field = err.path[2] as string;

    if (!attendeeFieldErrors[index]) {
      attendeeFieldErrors[index] = {};
    }

    attendeeFieldErrors[index][field] = err.message;
  } else {
    fieldErrors[err.path.join(".")] = err.message;
  }
});

        setErrors(fieldErrors);
        setAttendeeErrors(attendeeFieldErrors);
        setIsLoading(false);
        return;
      }
    }

    const payload = {
      fullName: formData.fullName,
      email: formData.email,
      title: formData.title,
      organization: formData.organization,
      phoneNumber: formData.phoneNumber,
      date: formData.date,
      sendEmail: true,
      agendaId: parseInt(formData.selectedAgenda),

      captchaToken: captchaToken,

      website: website,

      attendees: attendees.map((attendee) => ({
        name: attendee.name,
        email: attendee.email,
        department: attendee.department,
      })),
      ...(formData.additionalNotes && formData.additionalNotes.trim()
        ? { additionalNotes: formData.additionalNotes.trim() }
        : {}),
    };
    try {
       await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/meeting-request`,
        payload
      );
      onFormSubmit(); 
    } catch (error) {
      console.error("Error sending meeting request:", error);
      alert("Failed to send meeting request. Please try again.");
    } finally {
      setIsLoading(false); 
      setCaptchaToken(null);
      // captchaRef.current?.reset();
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="w-full max-w-[766px] h-auto bg-[#afafaf4f] rounded-[25px] p-8 font-body"
      style={{ fontSize: "15px" }}
    >
      <div className="flex flex-wrap justify-between gap-y-4">
        <div className="w-full md:w-[320px]">
          <input
            type="text"
            placeholder="Full name"
            name="fullName"
            value={formData.fullName}
            onChange={handleChange}
            className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none focus:ring-0 text-[#222222] placeholder:text-[#222222]"
          />
          {errors.fullName && (
            <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>
          )}
        </div>
        <div className="w-full md:w-[320px]">
          <input
            type="email"
            placeholder="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none focus:ring-0 placeholder:text-[#222222]"
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email}</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap justify-between mt-6 gap-y-4">
        <div className="w-full md:w-[320px]">
          <input
            type="text"
            placeholder="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none focus:ring-0 placeholder:text-[#222222]"
          />
          {errors.title && (
            <p className="text-red-500 text-sm mt-1">{errors.title}</p>
          )}
        </div>
        <div className="w-full md:w-[320px]">
          <input
            type="text"
            placeholder="Organization"
            name="organization"
            value={formData.organization}
            onChange={handleChange}
            className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none focus:ring-0 placeholder:text-[#222222]"
          />
          {errors.organization && (
            <p className="text-red-500 text-sm mt-1">{errors.organization}</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap justify-between mt-6 gap-y-4">
        <div className="w-full md:w-[280px]">
          <PhoneInput
            international
            defaultCountry="IN"
            value={formData.phoneNumber}
            onChange={handlePhoneChange}
            placeholder="Enter phone number"
            numberInputProps={{
              className:
                "w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none focus:ring-0",
            }}
            className="custom-phone-input"
          />
          {errors.phoneNumber && (
            <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>
          )}
        </div>
        <div className="w-full md:w-[320px]">
          <input
            type="date"
            placeholder="Date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none focus:ring-0"
          />
          {errors.date && (
            <p className="text-red-500 text-sm mt-1">{errors.date}</p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap justify-between mt-6 gap-y-4">
        <div className="w-full">
          <select
            name="selectedAgenda"
            value={formData.selectedAgenda}
            onChange={handleAgendaChange}
            className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none focus:ring-0"
          >
            <option value="">Select Agenda</option>
            {agendas.map((agenda) => (
              <option key={agenda.agendaId} value={agenda.agendaId}>
                {agenda.title}
              </option>
            ))}
          </select>
          {errors.selectedAgenda && (
            <p className="text-red-500 text-sm mt-1">{errors.selectedAgenda}</p>
          )}
        </div>
      </div>
      <h2 className="font-heading mt-25 text-[28px]" style={{ marginTop: "80px" }}>Additional Attendees</h2>
      {attendees.map((attendee, index) => (
        <div
          key={index}
          className="flex flex-wrap justify-between mt-4 items-start gap-y-4"
        >
     <div className="w-full md:w-[200px] flex flex-col">
  <input
    type="text"
    placeholder="Name"
    className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none"
    value={attendee.name}
    onChange={(e) =>
      handleAttendeeChange(index, "name", e.target.value)
    }
  />

  <div className="h-[18px]">
    {attendeeErrors[index]?.name && (
      <p className="text-red-500 text-sm">
        {attendeeErrors[index].name}
      </p>
    )}
  </div>
</div>
     <div className="w-full md:w-[200px] flex flex-col">
  <input
    type="email"
    placeholder="Email"
    className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none"
    value={attendee.email}
    onChange={(e) =>
      handleAttendeeChange(index, "email", e.target.value)
    }
  />

  <div className="h-[18px]">
    {attendeeErrors[index]?.email && (
      <p className="text-red-500 text-sm">
        {attendeeErrors[index].email}
      </p>
    )}
  </div>
</div>
  <div className="w-full md:w-[200px] flex flex-col">
  <input
    type="text"
    placeholder="Designation/Department"
    className="w-full py-2 border-0 border-b border-b-[#222222] focus:outline-none"
    value={attendee.department}
    onChange={(e) =>
      handleAttendeeChange(index, "department", e.target.value)
    }
  />

  <div className="h-[18px]">
    {attendeeErrors[index]?.department && (
      <p className="text-red-500 text-sm">
        {attendeeErrors[index].department}
      </p>
    )}
  </div>
</div>
          {attendees.length > 1 && (
            <button
              type="button"
              onClick={() => handleRemoveAttendee(index)}
              className="ml-2 text-[#222222B2] font-bold cursor-pointer"
            >
              X
            </button>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={handleAddAttendee}
        className="border border-dashed border-[##222222B2] mt-6 flex w-full items-center justify-center py-4 cursor-pointer"
        style={{
          borderWidth: "1px",
          borderSpacing: "10px",
          borderImage:
            "repeating-linear-gradient(90deg, ##222222B2 0, ##222222B2 10px, transparent 10px, transparent 20px) 1",
          marginTop: "25px"
        }}
      >
        + Add attendee
      </button>

      <div className="w-full mt-6">
        <textarea
          placeholder="Additional Information (Optional)"
          value={formData.additionalNotes}
          name="additionalNotes"
          onChange={handleChange}
          className="py-2 border-0 border-b border-b-[#222222] focus:outline-none focus:ring-0 w-full pb-6 placeholder:text-[#222222]"
        />
        {errors.additionalNotes && (
          <p className="text-red-500 text-sm mt-1">{errors.additionalNotes}</p>
        )}
      </div>
        {/* Captcha */}
  {/* <div className="flex justify-start">
    <ReCAPTCHA
      ref={captchaRef}
      sitekey={import.meta.env.VITE_SITE_KEY}
      onChange={handleCaptchaChange}
    />
  </div> */}
  <input
  type="text"
  name="website"
  onChange={(e) => setWebsite(e.target.value)}
  style={{ display: "none" }}
  autoComplete="off"
/>
      <div className="inline-flex items-center relative group mt-6 cursor-pointer rounded-full overflow-hidden">
        {/* Background overlay that slides */}
        <div className="absolute inset-0 rounded-full bg-[#EA5E20] w-0 group-hover:w-full transition-all duration-500 ease-in-out"></div>

        {/* Icon */}
        <div className="relative z-10 p-2 bg-[#EA5E20] text-white rounded-full">
          <FaArrowRight />
        </div>

        {/* Button */}
    <button
  type="submit"
  disabled={isLoading}
  className={`relative z-10 px-4 py-2 font-medium transition-colors duration-500`}
>
          {isLoading ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          ) : (
            "Submit"
          )}
        </button>
      </div>
    </form>
  );
};

export default MeetingRequestForm;
